import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { popoverContentClasses } from './Popover.styles';
import type { PopoverAlign, PopoverProps, PopoverSide } from './Popover.types';

const SIDE_OFFSET_DEFAULT = 8;

function getPosition(
  triggerRect: DOMRect,
  contentRect: DOMRect,
  side: PopoverSide,
  align: PopoverAlign,
  sideOffset: number
): { top: number; left: number } {
  const { width: tw, height: th, top: tt, left: tl, right: tr, bottom: tb } = triggerRect;
  const { width: cw, height: ch } = contentRect;
  const scrollX = 0;
  const scrollY = 0;

  let top = 0;
  let left = 0;

  const alignLeft = (): number => {
    if (align === 'start') {
      return tl;
    }
    if (align === 'end') {
      return tr - cw;
    }
    return tl + tw / 2 - cw / 2;
  };

  const alignTop = (): number => {
    if (align === 'start') {
      return tt;
    }
    if (align === 'end') {
      return tb - ch;
    }
    return tt + th / 2 - ch / 2;
  };

  switch (side) {
    case 'top':
      top = tt - ch - sideOffset + scrollY;
      left = alignLeft() + scrollX;
      break;
    case 'bottom':
      top = tb + sideOffset + scrollY;
      left = alignLeft() + scrollX;
      break;
    case 'left':
      top = alignTop() + scrollY;
      left = tl - cw - sideOffset + scrollX;
      break;
    case 'right':
      top = alignTop() + scrollY;
      left = tr + sideOffset + scrollX;
      break;
    default:
      top = tb + sideOffset + scrollY;
      left = alignLeft() + scrollX;
  }

  return { top, left };
}

function clampViewport(
  top: number,
  left: number,
  contentRect: DOMRect,
  sideOffset: number
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const padding = 8;
  let nextTop = top;
  let nextLeft = left;

  if (nextLeft < padding) {
    nextLeft = padding;
  }
  if (nextLeft + contentRect.width > vw - padding) {
    nextLeft = vw - contentRect.width - padding;
  }
  if (nextTop < padding) {
    nextTop = padding;
  }
  if (nextTop + contentRect.height > vh - padding) {
    nextTop = vh - contentRect.height - padding;
  }

  return { top: nextTop, left: nextLeft };
}

export function Popover({
  children,
  content,
  side = 'bottom',
  align = 'center',
  sideOffset = SIDE_OFFSET_DEFAULT,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  className,
  contentClassName,
  disabled = false
}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const setOpen = useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(value);
      }
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange]
  );

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !contentRef.current) {
      return;
    }
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();
    const pos = getPosition(triggerRect, contentRect, side, align, sideOffset);
    const clamped = clampViewport(pos.top, pos.left, contentRect, sideOffset);
    setPosition(clamped);
  }, [side, align, sideOffset]);

  useLayoutEffect(() => {
    if (open) {
      updatePosition();
      return;
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, closeOnEscape, setOpen]);

  useEffect(() => {
    if (!open || !closeOnOutsideClick) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      // Use composedPath() so clicks inside Shadow DOM (e.g. TailwindWrapper) are
      // not treated as outside. Across the shadow boundary, e.target is retargeted to the host.
      const path = e.composedPath?.() ?? [e.target];
      const isInside = path.some((node) => {
        if (!(node instanceof Node)) return false;
        return (
          triggerRef.current === node ||
          triggerRef.current?.contains(node) ||
          contentRef.current === node ||
          contentRef.current?.contains(node)
        );
      });
      if (isInside) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeOnOutsideClick, setOpen]);

  const handleTriggerClick = () => {
    if (disabled) {
      return;
    }
    setOpen(!open);
  };

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open, setOpen]);

  return (
    <>
      <div
        ref={triggerRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={handleTriggerClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTriggerClick();
          }
        }}
        className={className}
      >
        {children}
      </div>
      {open && (
        <div
          ref={contentRef}
          role="dialog"
          aria-modal="false"
          data-side={side}
          data-align={align}
          className={cn(popoverContentClasses({ visibility: position ? 'visible' : 'hidden' }), contentClassName)}
          style={{
            top: position?.top ?? 0,
            left: position?.left ?? 0,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      )}
    </>
  );
}
